import React, { JSX, PropsWithChildren, ReactElement, useCallback } from 'react';
import { DateWithKeyboardSupport } from './DateWithKeyboardSupport';
import { Modal, ModalBody, Row } from 'reactstrap';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faTimes } from '@fortawesome/free-solid-svg-icons';
import { DatePickerProps as ReactDatePickerProps } from 'react-datepicker';
import { ApiPackageModel } from '@ibe/api';
import Keys from '../../../Translations/generated/date-range-input.en.json.keys';
import { useTranslation } from 'react-i18next';
import { useTheme } from '@emotion/react';
import dayjs from 'dayjs';
import DateRangeDisplay from '../DateRangeInput/DateRangeDisplay';
import DataTestIdProp from '../DataTestIdProp';
import { useAppService, useConfig } from '@ibe/components';
import { OnChangeDate } from './CustomDisplayProps';

export interface CustomsProps {
  onInvalidInputFormat?: () => void;
}

export type DatePickerOnChange =
  | ((date: Date, evt?: React.MouseEvent<HTMLElement> | React.KeyboardEvent<HTMLElement>) => void)
  | ((
      date: [Date, Date],
      evt?: React.MouseEvent<HTMLElement> | React.KeyboardEvent<HTMLElement>
    ) => void);

export type DatePickerProps = {
  date?: Date;
  displayFormat?: string;
  withHeader?: boolean;
  noDatePickerOverflow?: boolean;
  customCalendarContainer?: ReactDatePickerProps['popperContainer'];
  selectsDisabledDaysInRange?: boolean;
  withKeyboardSupport?: boolean;
  inputClassName?: string;
  useFormValidation?: boolean;
  setValueToNullWhenFormatIsIncorrect?: boolean;
  availabilityPackage?: ApiPackageModel;
  updatePackageAvailability?: (date: Date) => void;
  showInModal?: boolean;
  modalLabel?: string;
  noInitialValueInput?: boolean;
  duration?: string;
  customDivider?: React.ReactNode;
  customDateFormat?: boolean;
  RenderDayContents?: ReactElement;
} & Omit<ReactDatePickerProps, 'onChange'> & {
    onChange: DatePickerOnChange;
  };

const DatePickerInModal = (
  props: PropsWithChildren<
    DatePickerProps &
      DataTestIdProp &
      CustomsProps & {
        onInputChange: (newDate: OnChangeDate | null) => void;
        toggleDropdown: () => void;
        isModalOpen: boolean;
        onChange: DatePickerOnChange;
      }
  >
): JSX.Element => {
  const { t } = useTranslation('datepicker');
  const app = useAppService();
  const config = useConfig();
  const theme = useTheme();
  const defaultDateFormat = config.displayFormatDate[app.lang] || 'DD/MM/YYYY';
  const {
    children,
    withKeyboardSupport,
    date,
    displayFormat,
    dataTestId,
    placeholderText,
    className,
    onInputChange,
    minDate,
    maxDate,
    inputClassName,
    useFormValidation,
    toggleDropdown,
    customDateFormat,
    duration = '',
    customDivider,
    isModalOpen,
    modalLabel,
    noInitialValueInput
  } = props;

  const dateToString = useCallback((dateObj: Date): string => {
    return dayjs(dateObj).format(config.formatDate.toUpperCase());
  }, []);

  return (
    <div className="iso__date-picker__modal-container">
      {withKeyboardSupport ? (
        <DateWithKeyboardSupport
          date={date}
          format={displayFormat || defaultDateFormat}
          theme={theme}
          dataTestId={dataTestId}
          placeholder={placeholderText || ''}
          className={className}
          onInputChange={onInputChange}
          minDate={minDate}
          maxDate={maxDate}
          inputClassName={inputClassName}
          useFormValidation={useFormValidation}
          onClick={toggleDropdown}
          noInitialValueInput={noInitialValueInput}
        />
      ) : (
        <DateRangeDisplay
          customDateFormat={customDateFormat}
          firstDate={props.startDate ? dateToString(props.startDate) : ''}
          secondDate={props.endDate ? dateToString(props.endDate) : ''}
          // displayIcon={theme.useCalenderIcon}
          displayIcon={false}
          fromTranslation={t(Keys.from)}
          untilTranslation={t(Keys.to)}
          dataTestId={dataTestId}
          displayDuration
          duration={duration}
          customDivider={customDivider}
          onClick={() => {
            toggleDropdown();
          }}
        />
      )}
      <Modal
        className="iso__date-picker__modal"
        isOpen={isModalOpen}
        toggle={toggleDropdown}
        container={
          config.outsideElementsContainerId
            ? window?.document?.getElementById(config.outsideElementsContainerId) || undefined
            : undefined
        }
      >
        <ModalBody>
          <Row className="justify-content-between align-items-center mb-2 px-2">
            <span className="iso__date-picker__modal__label">
              {modalLabel ?? t(Keys.datePickerModalHeader)}
            </span>
            <FontAwesomeIcon
              icon={faTimes}
              className="iso__date-picker__modal__close"
              onClick={(): void => toggleDropdown()}
            />
          </Row>
          <Row className="justify-content-center">{children}</Row>
        </ModalBody>
      </Modal>
    </div>
  );
};

export default DatePickerInModal;
